/**
 * Trial Countdown Component
 * Shows remaining trial days and upgrade CTA
 */

import React, {useState, useEffect} from 'react';
import {Clock, AlertTriangle, ArrowRight} from 'lucide-react';
import {Subscription, SubscriptionStatus} from '../../services/SubscriptionService';
import {useNavigate} from 'react-router-dom';

interface TrialCountdownProps {
  subscription: Subscription;
  onUpgrade?: () => void;
}

const TrialCountdown: React.FC<TrialCountdownProps> = ({subscription, onUpgrade}) => {
  const navigate = useNavigate();
  const [timeRemaining, setTimeRemaining] = useState<{
    days: number;
    hours: number;
    minutes: number;
    isExpired: boolean;
  } | null>(null);

  useEffect(() => {
    if (!subscription.trialEndDate) {
      return;
    }

    const updateCountdown = () => {
      const endDate = new Date(subscription.trialEndDate!);
      const now = new Date();
      
      // Validate dates - check if they're valid
      if (isNaN(endDate.getTime()) || isNaN(now.getTime())) {
        setTimeRemaining({days: 0, hours: 0, minutes: 0, isExpired: true});
        return;
      }
      
      const diff = endDate.getTime() - now.getTime();

      if (isNaN(diff) || diff <= 0) {
        setTimeRemaining({days: 0, hours: 0, minutes: 0, isExpired: true});
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      // Ensure values are valid numbers
      if (isNaN(days) || isNaN(hours) || isNaN(minutes)) {
        setTimeRemaining({days: 0, hours: 0, minutes: 0, isExpired: true});
        return;
      }

      setTimeRemaining({days, hours, minutes, isExpired: false});
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [subscription.trialEndDate]);

  if (!timeRemaining || subscription.status !== 'trial') {
    return null;
  }

  const isUrgent = timeRemaining.days <= 1 && !timeRemaining.isExpired;
  const isExpired = timeRemaining.isExpired;

  return (
    <div
      className={`relative overflow-hidden p-6 rounded-3xl border-2 backdrop-blur-xl ${
        isExpired
          ? 'bg-gradient-to-br from-red-50/80 to-rose-50/80 dark:from-red-900/20 dark:to-rose-900/20 border-red-200 dark:border-red-800/50 shadow-xl shadow-red-500/10'
          : isUrgent
            ? 'bg-gradient-to-br from-amber-50/80 to-yellow-50/80 dark:from-amber-900/20 dark:to-yellow-900/20 border-amber-200 dark:border-amber-800/50 shadow-xl shadow-amber-500/10'
            : 'bg-gradient-to-br from-blue-50/80 to-indigo-50/80 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800/50 shadow-xl shadow-blue-500/10'
      }`}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"></div>
      <div className="relative flex items-start">
        {isExpired ? (
          <div className="flex-shrink-0 p-3 rounded-xl bg-gradient-to-br from-red-500 to-rose-600 shadow-lg shadow-red-500/25 mr-4">
            <AlertTriangle className="w-6 h-6 text-white" />
          </div>
        ) : (
          <div className="flex-shrink-0 p-3 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg shadow-blue-500/25 mr-4">
            <Clock className="w-6 h-6 text-white" />
          </div>
        )}

        <div className="flex-1">
          <h3
            className={`text-2xl font-bold mb-2 ${
              isExpired
                ? 'text-red-900 dark:text-red-200'
                : isUrgent
                  ? 'text-amber-900 dark:text-amber-200'
                  : 'text-blue-900 dark:text-blue-200'
            }`}
          >
            {isExpired
              ? 'Trial Expired'
              : isUrgent
                ? 'Trial Ending Soon'
                : 'Free Trial Active'}
          </h3>

          {!isExpired && (
            <div className="mb-4">
              <p className="text-base text-slate-700 dark:text-slate-300 font-medium mb-1">
                {timeRemaining.days > 0 ? (
                  <>
                    <span className="font-bold text-3xl text-indigo-600 dark:text-indigo-400">{isNaN(timeRemaining.days) ? 0 : timeRemaining.days}</span>
                    <span className="ml-2">day{timeRemaining.days !== 1 ? 's' : ''} remaining</span>
                  </>
                ) : (
                  <>
                    <span className="font-bold text-3xl text-indigo-600 dark:text-indigo-400">{isNaN(timeRemaining.hours) ? 0 : timeRemaining.hours}</span>
                    <span className="ml-2">hour{timeRemaining.hours !== 1 ? 's' : ''} remaining</span>
                  </>
                )}
              </p>
            </div>
          )}

          {isExpired ? (
            <p className="text-base text-red-800 dark:text-red-300 mb-4 font-medium">
              Your trial has ended. Upgrade now to continue using Fitzaa.
            </p>
          ) : (
            <p className="text-base text-slate-600 dark:text-slate-400 mb-4 font-medium">
              Upgrade to a paid plan to continue after your trial ends.
            </p>
          )}

          <button
            onClick={() => {
              if (onUpgrade) {
                onUpgrade();
              } else {
                navigate(`/subscription/upgrade?org=${subscription.organizationId}`);
              }
            }}
            className={`group px-6 py-3 rounded-2xl font-semibold text-base transition-all duration-300 hover:scale-[1.02] flex items-center space-x-2 ${
              isExpired
                ? 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white shadow-lg shadow-red-500/25'
                : 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 text-white shadow-lg shadow-purple-500/25'
            }`}
          >
            <span>{isExpired ? 'Upgrade Now' : 'Upgrade to Continue'}</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrialCountdown;

